import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Scale, 
  Target, 
  TrendingUp, 
  TrendingDown,
  Calendar,
  Plus,
  Minus,
  Activity,
  Apple,
  Clock,
  ChevronRight,
  BarChart3,
  PieChart,
  Utensils
} from 'lucide-react';

interface WeightEntry {
  id: string;
  weight: number;
  date: Date;
  notes?: string;
  bodyFat?: number;
  muscleMass?: number;
}

interface MealPlanEntry {
  id: string;
  date: Date;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  completed: boolean;
}

interface WeightGoal {
  id: string;
  targetWeight: number;
  targetDate: Date;
  startWeight: number;
  startDate: Date;
  type: 'lose' | 'gain' | 'maintain';
  weeklyTarget: number;
}

interface WeightManagementData {
  currentWeight: number;
  goalWeight: number;
  weightHistory: WeightEntry[];
  mealPlan: MealPlanEntry[];
  goals: WeightGoal[];
  bmi: number;
  bodyFatPercentage: number;
  weeklyProgress: number;
}

const WeightManagementPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [weightData, setWeightData] = useState<WeightManagementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    loadWeightData();
  }, []);

  const loadWeightData = async () => {
    try {
      setLoading(true);
      const mockData: WeightManagementData = {
        currentWeight: 150,
        goalWeight: 140,
        bmi: 22.5,
        bodyFatPercentage: 18,
        weeklyProgress: -1.2,
        weightHistory: [
          { id: '1', weight: 152, date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), bodyFat: 19 },
          { id: '2', weight: 151, date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), bodyFat: 18.5 },
          { id: '3', weight: 150, date: new Date(), bodyFat: 18 }
        ],
        mealPlan: [
          {
            id: '1',
            date: new Date(),
            mealType: 'breakfast',
            name: 'Greek Yogurt Bowl with Berries',
            calories: 320,
            protein: 20,
            carbs: 35,
            fat: 8,
            completed: true
          },
          {
            id: '2',
            date: new Date(),
            mealType: 'lunch',
            name: 'Quinoa Power Salad',
            calories: 450,
            protein: 15,
            carbs: 60,
            fat: 12,
            completed: false
          },
          {
            id: '3',
            date: new Date(),
            mealType: 'snack',
            name: 'Post-Workout Protein Smoothie',
            calories: 280,
            protein: 25,
            carbs: 30,
            fat: 6,
            completed: false
          },
          {
            id: '4',
            date: new Date(),
            mealType: 'dinner',
            name: 'Grilled Salmon with Sweet Potato',
            calories: 520,
            protein: 35,
            carbs: 45,
            fat: 18,
            completed: false
          }
        ],
        goals: [
          {
            id: '1',
            targetWeight: 140,
            targetDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000),
            startWeight: 155,
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
            type: 'lose',
            weeklyTarget: 1
          }
        ]
      };
      setWeightData(mockData);
    } catch (error) {
      console.error('Error loading weight data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBMICategory = (bmi: number): { category: string; color: string } => {
    if (bmi < 18.5) return { category: 'Underweight', color: 'text-blue-600' };
    if (bmi < 25) return { category: 'Normal', color: 'text-green-600' };
    if (bmi < 30) return { category: 'Overweight', color: 'text-yellow-600' };
    return { category: 'Obese', color: 'text-red-600' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-primary)' }}></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto sculptd-container-padding space-y-8 md:space-y-12 sculptd-component" style={{ background: 'var(--color-background)' }}>
      {weightData && (
        <>
          {/* Header Title */}
          <div className="text-center space-y-4">
            <h1 className="text-3xl md:text-4xl font-bold" style={{ 
              color: 'var(--color-text)', 
              textShadow: '0 2px 4px rgba(0,0,0,0.1)' 
            }}>
              Weight Management
            </h1>
            <p className="text-lg md:text-xl" style={{ color: 'var(--color-text-muted)' }}>
              Track your progress and achieve your goals
            </p>
          </div>

          {/* Header Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="sculptd-card text-center relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, rgba(58, 175, 169, 0.1) 0%, rgba(58, 175, 169, 0.05) 100%)',
              boxShadow: 'var(--shadow-card-separation)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              backdropFilter: 'blur(20px)'
            }}>
              <CardContent className="sculptd-card-padding relative z-10">
                <div className="space-y-3">
                  <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center" style={{ 
                    background: 'var(--gradient-primary)', 
                    boxShadow: 'var(--shadow-glow)' 
                  }}>
                    <Scale className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-sm font-semibold uppercase tracking-wide" style={{ 
                    color: 'var(--color-text-muted)', 
                    letterSpacing: '0.5px' 
                  }}>
                    Current Weight
                  </p>
                  <div className="flex items-baseline justify-center space-x-1">
                    <p className="text-4xl md:text-5xl font-bold leading-none" style={{ 
                      color: 'var(--color-primary)', 
                      textShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                    }}>
                      {weightData.currentWeight}
                    </p>
                    <span className="text-lg font-medium" style={{ color: 'var(--color-text-muted)' }}>lbs</span>
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
              </CardContent>
            </Card>

            <Card className="sculptd-card text-center relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, rgba(156, 106, 222, 0.1) 0%, rgba(156, 106, 222, 0.05) 100%)',
              boxShadow: 'var(--shadow-card-separation)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              backdropFilter: 'blur(20px)'
            }}>
              <CardContent className="sculptd-card-padding relative z-10">
                <div className="space-y-3">
                  <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center" style={{ 
                    background: 'var(--gradient-accent)', 
                    boxShadow: 'var(--shadow-glow-secondary)' 
                  }}>
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-sm font-semibold uppercase tracking-wide" style={{ 
                    color: 'var(--color-text-muted)', 
                    letterSpacing: '0.5px' 
                  }}>
                    Goal Weight
                  </p>
                  <div className="flex items-baseline justify-center space-x-1">
                    <p className="text-4xl md:text-5xl font-bold leading-none" style={{ 
                      color: 'var(--color-accent)', 
                      textShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                    }}>
                      {weightData.goalWeight}
                    </p>
                    <span className="text-lg font-medium" style={{ color: 'var(--color-text-muted)' }}>lbs</span>
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
              </CardContent>
            </Card>

            <Card className="sculptd-card text-center relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, rgba(244, 182, 194, 0.1) 0%, rgba(244, 182, 194, 0.05) 100%)',
              boxShadow: '0 16px 64px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.05)',
              border: '1px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(20px)'
            }}>
              <CardContent className="sculptd-card-padding relative z-10">
                <div className="space-y-3">
                  <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center" style={{ 
                    background: 'var(--gradient-wellness)', 
                    boxShadow: '0 8px 24px rgba(244, 182, 194, 0.3)' 
                  }}>
                    <Activity className="w-8 h-8 text-white" />
                  </div>
                  <p className="text-sm font-semibold uppercase tracking-wide" style={{ 
                    color: 'var(--color-text-muted)', 
                    letterSpacing: '0.5px' 
                  }}>
                    BMI
                  </p>
                  <div className="flex items-baseline justify-center space-x-1">
                    <p className="text-4xl md:text-5xl font-bold leading-none" style={{ 
                      color: 'var(--color-wellness)', 
                      textShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                    }}>
                      {weightData.bmi}
                    </p>
                  </div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>
                    {getBMICategory(weightData.bmi).category}
                  </p>
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
              </CardContent>
            </Card>

            <Card className="sculptd-card text-center relative overflow-hidden" style={{
              background: weightData.weeklyProgress < 0 
                ? 'linear-gradient(135deg, rgba(163, 177, 138, 0.1) 0%, rgba(163, 177, 138, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(192, 128, 129, 0.1) 0%, rgba(192, 128, 129, 0.05) 100%)',
              boxShadow: '0 16px 64px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.05)',
              border: '1px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(20px)'
            }}>
              <CardContent className="sculptd-card-padding relative z-10">
                <div className="space-y-3">
                  <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center" style={{ 
                    background: weightData.weeklyProgress < 0 
                      ? 'linear-gradient(135deg, var(--color-success) 0%, rgba(163, 177, 138, 0.8) 100%)'
                      : 'linear-gradient(135deg, var(--color-dusty-rose) 0%, rgba(192, 128, 129, 0.8) 100%)',
                    boxShadow: weightData.weeklyProgress < 0 
                      ? '0 8px 24px rgba(163, 177, 138, 0.3)'
                      : '0 8px 24px rgba(192, 128, 129, 0.3)'
                  }}>
                    {weightData.weeklyProgress < 0 ? 
                      <TrendingDown className="w-8 h-8 text-white" /> :
                      <TrendingUp className="w-8 h-8 text-white" />
                    }
                  </div>
                  <p className="text-sm font-semibold uppercase tracking-wide" style={{ 
                    color: 'var(--color-text-muted)', 
                    letterSpacing: '0.5px' 
                  }}>
                    This Week
                  </p>
                  <div className="flex items-baseline justify-center space-x-1">
                    <p className="text-4xl md:text-5xl font-bold leading-none" style={{ 
                      color: weightData.weeklyProgress < 0 ? 'var(--color-success)' : 'var(--color-dusty-rose)', 
                      textShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                    }}>
                      {Math.abs(weightData.weeklyProgress)}
                    </p>
                    <span className="text-lg font-medium" style={{ color: 'var(--color-text-muted)' }}>lbs</span>
                  </div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>
                    {weightData.weeklyProgress < 0 ? 'Lost' : 'Gained'}
                  </p>
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex justify-center mb-8">
              <TabsList className="grid w-full max-w-2xl grid-cols-2 md:grid-cols-4 gap-2 bg-white/60 backdrop-blur-md rounded-2xl p-3" style={{
                boxShadow: '0 12px 40px rgba(0,0,0,0.1), 0 6px 20px rgba(0,0,0,0.05)',
                border: '1px solid rgba(255,255,255,0.3)'
              }}>
                <TabsTrigger value="overview" className="rounded-xl text-sm md:text-base font-semibold px-4 md:px-6 py-3 md:py-4 transition-all duration-300" style={{ minHeight: '48px' }}>Overview</TabsTrigger>
                <TabsTrigger value="tracking" className="rounded-xl text-sm md:text-base font-semibold px-4 md:px-6 py-3 md:py-4 transition-all duration-300" style={{ minHeight: '48px' }}>Weight Tracking</TabsTrigger>
                <TabsTrigger value="meals" className="rounded-xl text-sm md:text-base font-semibold px-4 md:px-6 py-3 md:py-4 transition-all duration-300" style={{ minHeight: '48px' }}>Meal Planning</TabsTrigger>
                <TabsTrigger value="goals" className="rounded-xl text-sm md:text-base font-semibold px-4 md:px-6 py-3 md:py-4 transition-all duration-300" style={{ minHeight: '48px' }}>Goals</TabsTrigger>
              </TabsList>
            </div>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-8 md:space-y-10 mt-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="sculptd-card relative overflow-hidden" style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  boxShadow: '0 16px 64px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.05)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  backdropFilter: 'blur(20px)'
                }}>
                  <CardHeader className="pb-6 relative z-10">
                    <CardTitle className="text-xl md:text-2xl font-bold flex items-center gap-3" style={{ 
                      color: 'var(--color-text)', 
                      textShadow: '0 2px 4px rgba(0,0,0,0.1)' 
                    }}>
                      <div className="p-3 rounded-xl" style={{ 
                        background: 'var(--gradient-primary)', 
                        boxShadow: '0 4px 16px rgba(58, 175, 169, 0.3)' 
                      }}>
                        <BarChart3 className="h-6 w-6 text-white" />
                      </div>
                      Weight Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="sculptd-card-padding relative z-10">
                    <div className="space-y-6">
                      <div className="text-center">
                        <div className="text-5xl font-bold mb-2" style={{ color: 'var(--color-primary)' }}>
                          {Math.round(((weightData.goals[0]?.startWeight - weightData.currentWeight) / (weightData.goals[0]?.startWeight - weightData.goalWeight)) * 100)}%
                        </div>
                        <p className="text-lg font-medium" style={{ color: 'var(--color-text-muted)' }}>Progress to Goal</p>
                      </div>
                      <div className="relative">
                        <div className="w-full h-4 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.1)' }}>
                          <div 
                            className="h-full rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
                            style={{ 
                              background: 'var(--gradient-primary)',
                              width: `${Math.min(100, Math.max(0, ((weightData.goals[0]?.startWeight - weightData.currentWeight) / (weightData.goals[0]?.startWeight - weightData.goalWeight)) * 100))}%`,
                              boxShadow: '0 2px 8px rgba(58, 175, 169, 0.3)'
                            }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"></div>
                          </div>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-base font-medium" style={{ color: 'var(--color-text-muted)' }}>
                          {weightData.currentWeight - weightData.goalWeight} lbs to go
                        </p>
                      </div>
                    </div>
                  </CardContent>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
                </Card>

                <Card className="sculptd-card relative overflow-hidden" style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  boxShadow: '0 16px 64px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.05)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  backdropFilter: 'blur(20px)'
                }}>
                  <CardHeader className="pb-6 relative z-10">
                    <CardTitle className="text-xl md:text-2xl font-bold flex items-center gap-3" style={{ 
                      color: 'var(--color-text)', 
                      textShadow: '0 2px 4px rgba(0,0,0,0.1)' 
                    }}>
                      <div className="p-3 rounded-xl" style={{ 
                        background: 'var(--gradient-wellness)', 
                        boxShadow: '0 4px 16px rgba(244, 182, 194, 0.3)' 
                      }}>
                        <PieChart className="h-6 w-6 text-white" />
                      </div>
                      Body Composition
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="sculptd-card-padding relative z-10">
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { label: 'Body Fat', value: weightData.bodyFatPercentage, unit: '%', color: 'var(--color-wellness)', bg: 'rgba(244, 182, 194, 0.1)' },
                          { label: 'Muscle Mass', value: Math.round(weightData.currentWeight * (1 - weightData.bodyFatPercentage / 100)), unit: 'lbs', color: 'var(--color-primary)', bg: 'rgba(58, 175, 169, 0.1)' }
                        ].map((item, idx) => (
                          <div key={idx} className="text-center sculptd-card-padding rounded-xl relative overflow-hidden" style={{
                            background: item.bg,
                            border: '1px solid rgba(255,255,255,0.3)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
                          }}>
                            <p className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ 
                              color: 'var(--color-text-muted)', 
                              letterSpacing: '0.5px' 
                            }}>
                              {item.label}
                            </p>
                            <div className="flex items-baseline justify-center space-x-1">
                              <p className="text-2xl md:text-3xl font-bold" style={{ 
                                color: item.color, 
                                textShadow: '0 2px 4px rgba(0,0,0,0.1)' 
                              }}>
                                {item.value}
                              </p>
                              <span className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>
                                {item.unit}
                              </span>
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none"></div>
                          </div>
                        ))}
                      </div>
                      <div className="text-center sculptd-card-padding rounded-xl" style={{
                        background: 'rgba(156, 106, 222, 0.1)',
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}>
                        <p className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ 
                          color: 'var(--color-text-muted)', 
                          letterSpacing: '0.5px' 
                        }}>
                          BMI Category
                        </p>
                        <Badge className="text-base font-bold px-4 py-2" style={{ 
                          background: 'var(--gradient-accent)',
                          color: 'white',
                          border: 'none'
                        }}>
                          {getBMICategory(weightData.bmi).category}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
                </Card>
              </div>
            </TabsContent>

            {/* Weight Tracking Tab */}
            <TabsContent value="tracking" className="space-y-6 md:space-y-8 mt-6">
              <Card className="sculptd-card-elevated sculptd-sheen border-0" style={{
                boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)'
              }}>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 text-xl font-bold" style={{ 
                      color: 'var(--color-text)',
                      textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <Calendar className="w-6 h-6" style={{ color: 'var(--color-primary)' }} />
                      <span>Weight History</span>
                    </div>
                    <Button size="sm" className="sculptd-button-primary sculptd-sheen flex items-center justify-center" style={{ 
                      background: 'var(--gradient-cta-primary)',
                      boxShadow: 'var(--shadow-lg)',
                      minHeight: '44px',
                      borderRadius: '24px',
                      padding: '12px 24px',
                      fontWeight: '600',
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Entry
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="sculptd-card-padding">
                  <div className="space-y-4">
                    {weightData.weightHistory.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between p-6 rounded-lg transition-colors" style={{ background: 'var(--color-background)' }} onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-background-secondary)'} onMouseLeave={(e) => e.currentTarget.style.background = 'var(--color-background)'}>
                        <div className="flex items-center space-x-4">
                          <div className="p-2 rounded-full" style={{ background: 'var(--color-primary-light)' }}>
                            <Scale className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
                          </div>
                          <div>
                            <p className="font-semibold">{entry.weight} lbs</p>
                            <p className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{entry.date.toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {entry.bodyFat && (
                            <p className="text-sm font-medium" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{entry.bodyFat}% body fat</p>
                          )}
                          <ChevronRight className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Meal Planning Tab */}
            <TabsContent value="meals" className="space-y-6 md:space-y-8 mt-6">
              <Card className="sculptd-card-elevated sculptd-sheen border-0" style={{
                boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)'
              }}>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 text-xl font-bold" style={{ 
                      color: 'var(--color-text)',
                      textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <Utensils className="w-6 h-6" style={{ color: 'var(--color-secondary)' }} />
                      <span>Curated Recipe Cards</span>
                    </div>
                    <Button size="sm" className="sculptd-button-secondary sculptd-sheen" style={{ 
                      background: 'var(--gradient-cta-secondary)',
                      boxShadow: 'var(--shadow-lg)',
                      minHeight: '44px'
                    }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Recipe
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="sculptd-card-padding">
                  <div className="space-y-4">
                    {weightData.mealPlan.map((meal) => (
                      <div key={meal.id} className="flex items-center justify-between sculptd-card-padding rounded-xl relative overflow-hidden transition-all hover:shadow-lg" style={{
                        background: meal.completed 
                          ? 'linear-gradient(135deg, rgba(58, 175, 169, 0.15) 0%, rgba(156, 106, 222, 0.1) 100%)' 
                          : 'linear-gradient(135deg, rgba(244, 182, 194, 0.1) 0%, rgba(156, 106, 222, 0.05) 100%)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                      }}>
                        <div className="flex items-center space-x-4">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${meal.completed ? 'bg-green-500' : 'bg-gradient-to-r from-pink-400 to-purple-400'}`}>
                            {meal.completed && <span className="text-white text-xs">✓</span>}
                          </div>
                          <div>
                            <p className="font-bold text-lg">{meal.name}</p>
                            <p className="text-sm capitalize font-medium" style={{ 
                              color: 'var(--color-lavender)',
                              textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}>{meal.mealType}</p>
                            <p className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                              {meal.calories} cal • {meal.protein}g protein • {meal.carbs}g carbs • {meal.fat}g fat
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-xl" style={{ color: 'var(--color-primary)' }}>{meal.calories}</p>
                          <p className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>cal • {meal.protein}g protein</p>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent pointer-events-none"></div>
                      </div>
                    ))}
                    
                    {/* Auto-suggestion card */}
                    <div className="mt-6 sculptd-card-padding rounded-xl relative overflow-hidden" style={{
                      background: 'var(--gradient-celebration)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 8px 24px rgba(156, 106, 222, 0.2)'
                    }}>
                      <div className="flex items-center space-x-3 text-white">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                          <span className="text-sm font-bold">💡</span>
                        </div>
                        <div>
                          <p className="font-semibold">Need 20g more protein?</p>
                          <p className="text-sm opacity-90">Try our Post-Workout Protein Smoothie</p>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent pointer-events-none"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Goals Tab */}
            <TabsContent value="goals" className="space-y-6 md:space-y-8 mt-6">
              <Card className="sculptd-card-elevated sculptd-sheen border-0" style={{
                boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)'
              }}>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 text-xl font-bold" style={{ 
                      color: 'var(--color-text)',
                      textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <Target className="w-6 h-6" style={{ color: 'var(--color-accent)' }} />
                      <span>Weight Goals</span>
                    </div>
                    <Button size="sm" className="sculptd-button-accent sculptd-sheen" style={{ 
                      background: 'var(--gradient-accent)',
                      minHeight: '44px'
                    }}>
                      <Plus className="w-4 h-4 mr-2" />
                      New Goal
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="sculptd-card-padding">
                  <div className="space-y-6">
                    {weightData.goals.map((goal) => (
                      <div key={goal.id} className="sculptd-card-elevated sculptd-card-padding rounded-lg hover:shadow-xl transition-all duration-300" style={{
                        boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
                      }}>
                        <div className="flex items-center justify-between mb-6">
                          <div className="space-y-2">
                            <h4 className="font-bold text-xl" style={{ 
                              color: 'var(--color-text)',
                              textShadow: '0 1px 2px rgba(0,0,0,0.08)'
                            }}>
                              {goal.type === 'lose' ? 'Weight Loss' : goal.type === 'gain' ? 'Weight Gain' : 'Maintain Weight'}
                            </h4>
                            <p className="text-base" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                              Target: {goal.targetWeight} lbs by {goal.targetDate.toLocaleDateString()}
                            </p>
                          </div>
                          <Badge className="sculptd-sheen" style={{ 
                            background: 'var(--color-accent)', 
                            color: 'white',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}>
                            {goal.weeklyTarget} lbs/week
                          </Badge>
                        </div>
                        <div className="space-y-4">
                          <div className="flex justify-between text-base font-medium" style={{ 
                            color: 'var(--color-text)',
                            textShadow: '0 1px 2px rgba(0,0,0,0.05)' 
                          }}>
                            <span>Progress</span>
                            <span>{Math.round(((goal.startWeight - weightData.currentWeight) / (goal.startWeight - goal.targetWeight)) * 100)}%</span>
                          </div>
                          <div className="w-full rounded-full h-3" style={{ background: 'var(--color-background-secondary)' }}>
                            <div 
                              className="h-3 rounded-full transition-all duration-500"
                              style={{ 
                                background: 'var(--gradient-accent)',
                                width: `${Math.min(100, Math.max(0, ((goal.startWeight - weightData.currentWeight) / (goal.startWeight - goal.targetWeight)) * 100))}%`
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default WeightManagementPanel;
